// "Purchasing plan" on a bought sell item (item panel, and the detail panel on the Purchasing plan
// page): where it stands (bucket, order-by date, suggestion and why), its planning settings (policy,
// stock policy, safety stock, in-stock target, snooze) and wholesale/B2B demand. Monthly overrides are
// in the Forecast section (ForecastSection.tsx).
import { ChevronDown, ChevronRight, Moon, Plus, Trash2 } from "lucide-react";
import { useState } from "react";
import type { B2bDemand as B2bDemandRow, Item } from "../../../shared/db.gen.ts";
import type { MutationArgs } from "../../../shared/mutations.ts";
import { POLICIES, POLICY_LABELS, STOCK_POLICIES, STOCK_POLICY_LABELS } from "../../../shared/mutations-planning.ts";
import { addPlanDays, BUCKET_LABELS, type Bucket, coverText, explain, fmt, type ItemPlanResult, orderByText, shortDate } from "../../../shared/planning.ts";
import { stockNote } from "../../../shared/stock.ts";
import { money, qty, typedCount } from "../lib/format.ts";
import { can, mutate } from "../lib/mutate.ts";
import { saveItemPlan, usePlan } from "../lib/planning.ts";
import { Link } from "../lib/router.tsx";
import { useRow, useRows } from "../lib/sync.ts";
import { Badge, Button, cx, Input, StatTile } from "../ui/controls.tsx";
import { type Column, DataTable } from "../ui/DataTable.tsx";
import { Fields, Section } from "../ui/RecordPanel.tsx";
import { toast } from "../ui/toast.tsx";
import { ClosureChip } from "./Closures.tsx";
import { StockTimeline } from "./StockTimeline.tsx";

export const BUCKET_TONE: Record<Bucket, "neutral" | "accent" | "ok" | "warn" | "danger"> = {
  order_now: "danger",
  at_risk: "warn",
  this_month: "accent",
  upcoming: "neutral",
  overstock: "neutral",
  ok: "ok",
  snoozed: "neutral",
  not_planned: "neutral",
};

export function ItemPlanSection({ item, inPlan = false }: { item: Item; inPlan?: boolean }) {
  const plan = usePlan();
  const r = plan.byItem.get(item.id);
  if (!r) return null; // not a bought sell item, a make item or a part in one's BOM
  return (
    <>
      <PlanSummary r={r} inPlan={inPlan} />
      {r.planned && <StockTimeline r={r} today={plan.today} />} {/* Projected stock --- day by day against safety stock */}
      {r.planned && <B2bDemand itemId={item.id} />}
    </>
  );
}

function PlanSummary({ r, inPlan }: { r: ItemPlanResult; inPlan: boolean }) {
  const plan = usePlan();
  const ip = useRow("item_plan", r.item.id);
  const [whyOpen, setWhyOpen] = useState(inPlan);
  const editable = can("item_plan.update");
  const why = explain(r, plan);
  const classTarget = r.cls === "A" ? plan.settings.service_a : r.cls === "B" ? plan.settings.service_b : plan.settings.service_c;
  const settingsRow = {
    id: r.item.id,
    policy: r.policy,
    stock_policy: ip?.stock_policy ?? "forecast",
    min_qty: ip?.min_qty ?? null,
    max_qty: ip?.max_qty ?? null,
    target_qty: ip?.target_qty ?? null,
    safety_stock: ip?.safety_stock ?? null,
    service_level: ip?.service_level ?? null,
  };
  // Switching to min/max or a target fills in a starting point (safety stock, plus an order cycle of the forecast) if none is set.
  const floor = Math.ceil(r.safetyFrom === "policy" ? 0 : r.safetyStock);
  const top = Math.ceil(floor + r.rate * r.coverFor);
  const setStockPolicy = (v: (typeof STOCK_POLICIES)[number]) =>
    saveItemPlan(r.item.id, {
      stock_policy: v,
      ...(v === "min_max" && (ip?.min_qty == null || ip?.max_qty == null) ? { min_qty: floor, max_qty: Math.max(floor, top) } : {}),
      ...(v === "target" && ip?.target_qty == null ? { target_qty: top } : {}),
    });
  const units = (key: "min_qty" | "max_qty" | "target_qty", label: string, hint: string) => ({
    key,
    label,
    type: "number" as const,
    placeholder: hint,
    save: (v: unknown) => (v == null ? toast.error(`${label} can't be empty while this policy is on`) : saveItemPlan(r.item.id, { [key]: v as number })),
  });
  return (
    <Section title={r.role === "make" ? "Make plan" : "Purchasing plan"}>
      <div className="flex flex-wrap items-center gap-1.5 px-2 pb-2">
        <Badge tone={BUCKET_TONE[r.bucket]}>{BUCKET_LABELS[r.bucket]}</Badge>
        {r.atRisk && r.bucket !== "at_risk" && <Badge tone="warn">At risk</Badge>}
        {r.cls && <Badge>Class {r.cls}</Badge>}
        <span className="text-muted">
          {r.qty > 0 ? (
            <>
              Order <span className="font-medium text-text">{qty(r.qty)}</span>
              {r.orderByDay !== null &&
                (r.orderByDay <= 0 ? ` ${orderByText(r, plan.today).replace(/^Now|^Today/, (m) => m.toLowerCase())}` : ` by ${orderByText(r, plan.today)}`)}
              {r.cost != null && ` · ${money(r.cost, r.currency)}`}
              <ClosureChip delay={r.delay} who={r.vendor ? r.vendor.short_name || r.vendor.name : undefined} />
            </>
          ) : r.planned ? (
            "Nothing to order"
          ) : (
            r.skip
          )}
        </span>
        {!inPlan && r.planned && (
          <Link to={`${r.role === "make" ? "/make" : "/purchasing"}?item=${r.item.id}`} className="ml-auto text-[12px] text-accent-text hover:underline">
            Open in plan
          </Link>
        )}
      </div>
      {r.planned && (
        <div className="grid grid-cols-4 gap-2 px-2 pb-2">
          {r.item.kind === "sell" || !r.dependent.length ? (
            <StatTile label="Forecast / day" value={fmt(r.rate)} hint={`Next 90 days average; next 30 days: ${fmt(Math.round(r.next30))}`} />
          ) : (
            <StatTile label="Builds use / day" value={fmt(r.depRate)} hint={`${fmt(Math.round(r.dependentQty))} for the builds planned over the next year`} />
          )}
          <StatTile
            label="Cover"
            value={coverText(r)}
            hint={r.stockoutDay !== null ? `Runs out ${shortDate(addPlanDays(plan.today, r.stockoutDay), plan.today)}` : undefined}
          />
          <StatTile
            label="Available / in"
            value={`${fmt(r.available)} / ${fmt(r.incomingQty)}`}
            hint={[
              ...[stockNote(r)].filter((n): n is string => !!n),
              ...r.incoming.map((x) => `${x.poNumber}: ${fmt(x.qty)}`),
              ...(r.atSites > 0 ? [`${fmt(Math.round(r.atSites))} more at the CMs, for their builds`] : []),
            ].join(", ")}
          />
          <StatTile
            label={r.stockPolicy === "forecast" ? "Safety stock" : r.stockPolicy === "min_max" ? "Min / max" : "Target"}
            value={r.stockPolicy === "min_max" ? `${fmt(r.minQty ?? 0)} / ${fmt(r.maxQty ?? 0)}` : fmt(Math.ceil(r.safetyStock))}
            hint={r.stockPolicy === "forecast" ? `${fmt(r.serviceLevel)}% in-stock target` : undefined}
          />
        </div>
      )}
      {r.flags.length > 0 && (
        <ul className="mx-2 mb-2 rounded-md bg-warn-soft px-3 py-1.5 text-[12px] text-warn">
          {r.flags.map((f) => (
            <li key={f.code}>{f.message}</li>
          ))}
        </ul>
      )}
      {r.planned && (
        <div className="px-2 pb-1">
          <button type="button" onClick={() => setWhyOpen(!whyOpen)} className="flex items-center gap-1 text-[12px] text-muted hover:text-text">
            {whyOpen ? <ChevronDown className="size-3.5" /> : <ChevronRight className="size-3.5" />} Why
          </button>
          {whyOpen && (
            <ol className="mt-1 mb-1 ml-1 list-decimal space-y-0.5 pl-4 text-[12px] text-muted">
              {why.map((line) => (
                <li key={line}>{line}</li>
              ))}
            </ol>
          )}
        </div>
      )}
      <Fields
        compact
        row={settingsRow}
        disabled={!editable}
        fields={[
          {
            key: "policy",
            label: "Planning",
            type: "select",
            options: POLICIES.map((p) => ({ value: p, label: POLICY_LABELS[p] })),
            save: (v) => saveItemPlan(r.item.id, { policy: v as (typeof POLICIES)[number] }),
          },
          {
            key: "stock_policy",
            label: "Stock policy",
            type: "select",
            options: STOCK_POLICIES.map((p) => ({ value: p, label: STOCK_POLICY_LABELS[p] })),
            save: (v) => setStockPolicy(v as (typeof STOCK_POLICIES)[number]),
          },
          ...(settingsRow.stock_policy === "min_max" ? [units("min_qty", "Min", "Order when below"), units("max_qty", "Max", "Top up to")] : []),
          ...(settingsRow.stock_policy === "target" ? [units("target_qty", "Target", "Keep topped up to")] : []),
          // Safety stock only applies to the forecast policy (min/max and target set their own floor).
          ...(settingsRow.stock_policy === "forecast"
            ? [
                {
                  key: "safety_stock",
                  label: "Safety stock",
                  type: "number" as const,
                  placeholder: r.safetyFrom === "computed" ? `From sales: ${fmt(Math.ceil(r.safetyStock))}` : "From sales",
                  save: (v: unknown) => saveItemPlan(r.item.id, { safety_stock: v as number | null }),
                },
                {
                  key: "service_level",
                  label: "In-stock %",
                  type: "number" as const,
                  placeholder: `Class ${r.cls ?? "C"}: ${fmt(classTarget)}%`,
                  format: (v: unknown) => (v == null ? "" : `${v}%`),
                  save: (v: unknown) => saveItemPlan(r.item.id, { service_level: v as number | null }),
                },
              ]
            : []),
        ]}
      />
      {r.planned && <Snooze r={r} disabled={!editable} />}
    </Section>
  );
}

export const SNOOZE_PICKS: [string, number][] = [
  ["1 week", 7],
  ["2 weeks", 14],
  ["1 month", 30],
  ["3 months", 91],
];

/** Snooze form: until when (quick picks or a date) and why. */
export function SnoozeForm({ itemId, today, onDone }: { itemId: string; today: string; onDone: () => void }) {
  const [until, setUntil] = useState(addPlanDays(today, 14));
  const [reason, setReason] = useState("");
  const save = async () => {
    if (!reason.trim()) return toast.error("Say why you're snoozing it");
    if (until <= today) return toast.error("Pick a day after today");
    if (await saveItemPlan(itemId, { snooze_until: until, snooze_reason: reason.trim() })) onDone();
  };
  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap gap-1">
        {SNOOZE_PICKS.map(([label, days]) => (
          <Button
            key={label}
            size="sm"
            variant={until === addPlanDays(today, days) ? "primary" : "secondary"}
            onClick={() => setUntil(addPlanDays(today, days))}
          >
            {label}
          </Button>
        ))}
        <Input type="date" value={until} min={addPlanDays(today, 1)} onChange={(e) => setUntil(e.target.value)} className="h-7 w-36" />
      </div>
      <Input
        autoFocus
        value={reason}
        placeholder="Why? (e.g. new mold coming, waiting on a quote)"
        onChange={(e) => setReason(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") save();
          if (e.key === "Escape") onDone();
        }}
      />
      <div className="flex justify-end gap-2">
        <Button size="sm" variant="ghost" onClick={onDone}>
          Cancel
        </Button>
        <Button size="sm" variant="primary" icon={<Moon className="size-3.5" />} onClick={save}>
          Snooze until {shortDate(until, today)}
        </Button>
      </div>
    </div>
  );
}

function Snooze({ r, disabled }: { r: ItemPlanResult; disabled: boolean }) {
  const plan = usePlan();
  const [open, setOpen] = useState(false);
  if (r.snoozedUntil) {
    return (
      <div className="mx-2 mt-1 flex items-center gap-2 rounded-md bg-surface-2 px-2.5 py-1.5">
        <Moon className="size-3.5 text-muted" />
        <span className="min-w-0 flex-1">
          Snoozed until {shortDate(r.snoozedUntil, plan.today)}
          {r.snoozeReason && <span className="text-muted">: {r.snoozeReason}</span>}
        </span>
        {!disabled && (
          <Button size="sm" variant="ghost" onClick={() => saveItemPlan(r.item.id, { snooze_until: null, snooze_reason: "" })}>
            Unsnooze
          </Button>
        )}
      </div>
    );
  }
  if (disabled) return null;
  return open ? (
    <div className="mx-2 mt-1 rounded-md border border-line p-2">
      <SnoozeForm itemId={r.item.id} today={plan.today} onDone={() => setOpen(false)} />
    </div>
  ) : (
    <div className="px-2 pt-1">
      <Button size="sm" variant="ghost" icon={<Moon className="size-3.5" />} onClick={() => setOpen(true)}>
        Snooze…
      </Button>
    </div>
  );
}

function B2bDemand({ itemId }: { itemId: string }) {
  const plan = usePlan();
  const rows = useRows("b2b_demand")
    .filter((b) => b.item_id === itemId)
    .sort((a, b) => Number(a.done) - Number(b.done) || a.due_date.localeCompare(b.due_date));
  const editable = can("b2b_demand.create");
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState({ qty: "", due: addPlanDays(plan.today, 30), note: "" });

  const add = async () => {
    const n = typedCount(draft.qty);
    if (!n) return toast.error("Enter how many units");
    if (await mutate("b2b_demand.create", { id: crypto.randomUUID(), item_id: itemId, qty: n, due_date: draft.due, note: draft.note.trim() })) {
      setDraft({ qty: "", due: draft.due, note: "" });
      setAdding(false);
    }
  };
  const update = (id: string, patch: MutationArgs<"b2b_demand.update">["patch"]) => mutate("b2b_demand.update", { id, patch });
  const [slot, setSlot] = useState<HTMLDivElement | null>(null); // Table views --- by the section's title
  // A shipped order is faded: no longer counted
  const faded = (b: B2bDemandRow, className?: string) => cx(className, b.done && "opacity-50");
  const columns: Column<B2bDemandRow>[] = [
    {
      key: "due_date",
      header: "Due",
      width: "100px",
      // Open orders by due date, then the shipped ones
      value: (b) => `${b.done ? 1 : 0}${b.due_date}`,
      text: (b) => b.due_date,
      cell: (b) => (
        <span className={faded(b)} title={b.due_date}>
          {shortDate(b.due_date, plan.today)}
        </span>
      ),
      edit: {
        can: () => editable,
        get: (b) => b.due_date,
        save: (b, text) => {
          const t = text.trim();
          if (!t) return;
          if (!/^\d{4}-\d{2}-\d{2}$/.test(t)) return toast.error("Use a date like 2026-12-31");
          update(b.id, { due_date: t });
        },
      },
    },
    {
      key: "qty",
      header: "Units",
      width: "80px",
      align: "right",
      value: (b) => Number(b.qty),
      cell: (b) => <span className={faded(b, "font-mono")}>{qty(b.qty)}</span>,
      edit: {
        can: () => editable,
        get: (b) => String(b.qty),
        save: (b, text) => {
          const n = typedCount(text);
          if (!n) return toast.error("Units must be more than 0");
          update(b.id, { qty: n });
        },
      },
    },
    {
      key: "note",
      header: "For / note",
      headerTitle: "Customer, their PO…",
      width: "minmax(120px,1fr)",
      cell: (b) => (
        <span className={faded(b, "truncate")} title={b.note}>
          {b.note}
        </span>
      ),
      edit: { can: () => editable, get: (b) => b.note, save: (b, text) => update(b.id, { note: text }) },
    },
    {
      key: "done",
      header: "Shipped",
      headerTitle: "Shipped: no longer counted",
      width: "76px",
      cell: (b) => (
        <input
          type="checkbox"
          checked={b.done}
          disabled={!editable}
          title="Shipped: no longer counted"
          onClick={(e) => e.stopPropagation()}
          onChange={(e) => update(b.id, { done: e.target.checked })}
          className="accent-[var(--accent)]"
        />
      ),
    },
    {
      key: "remove",
      header: "",
      width: "36px",
      value: () => "",
      cell: (b) =>
        editable && (
          <button
            type="button"
            title="Remove"
            className="rounded p-1 text-faint hover:text-danger"
            onClick={(e) => {
              e.stopPropagation();
              mutate("b2b_demand.remove", { id: b.id });
            }}
          >
            <Trash2 className="size-3.5" />
          </button>
        ),
    },
  ];

  return (
    <Section
      title="Wholesale / B2B orders"
      count={rows.filter((b) => !b.done).length}
      slot={setSlot}
      addLabel="Add"
      onAdd={editable ? () => setAdding(true) : undefined}
    >
      {!rows.length && !adding && <div className="px-2 pb-2 text-muted">None. Known orders count as demand on their due date.</div>}
      {rows.length > 0 && (
        <div className="px-2 pb-2">
          <DataTable
            fit
            rows={rows}
            columns={columns}
            defaultSort={{ key: "due_date" }}
            editing={editable}
            rowNoun={["order", "orders"]}
            exportName="B2B orders"
            viewsSlot={slot}
            pageFilters={false}
          />
        </div>
      )}
      {adding && (
        <div className="grid grid-cols-[130px_80px_1fr_auto] items-center gap-x-2 px-2 pb-2 text-[12px]">
          <Input type="date" value={draft.due} onChange={(e) => setDraft({ ...draft, due: e.target.value })} className="h-7" />
          <Input
            autoFocus
            value={draft.qty}
            placeholder="Units"
            onChange={(e) => setDraft({ ...draft, qty: e.target.value })}
            onKeyDown={(e) => e.key === "Enter" && add()}
            className="h-7 text-right font-mono"
          />
          <Input
            value={draft.note}
            placeholder="Customer, their PO…"
            onChange={(e) => setDraft({ ...draft, note: e.target.value })}
            onKeyDown={(e) => {
              if (e.key === "Enter") add();
              if (e.key === "Escape") setAdding(false);
            }}
            className="h-7"
          />
          <Button size="sm" variant="primary" onClick={add} className="px-1.5">
            Add
          </Button>
        </div>
      )}
    </Section>
  );
}
